import React, { useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { XIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormModal } from '@/components/ui/form-modal';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import {
  useAddUsersToIncidentRoster,
  useGetPositionsForIncidentId,
  useGetRosterForIncident,
  useGetRostersForGroup,
  useRemoveMemberFromPositionInRoster,
} from 'hooks/incident/useRoster';

type Reviewer = {
  positionName: string;
  /** Display title that may mark a missing ICS seat. */
  title: string;
  /** `ics_position.id` when the seat exists on this incident. */
  positionId: string | null;
  users: {
    userGuid: string;
    displayName: string;
    incidentMemberId: string | null;
    /**
     * Roster occupant for this approval seat, not someone added from another
     * seat.
     */
    isOriginal: boolean;
  }[];
};

type AorReviewer = {
  reviewerItemId: string;
  displayName: string;
};

type RequestApprovalConfirmationModalProps = {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  name: string;
  createdBy: string;
  createdAt: string;
  editedBy: string;
  editedAt: string;
  incidentId: string | null;
  groupId?: string | null;
  aorReviewers?: AorReviewer[];
  /**
   * Ordered steps from the area’s approval policy. Replaces the
   * eligible-reviewer list.
   */
  approvalSteps?: { title: string; people: string[] }[];
  reviewersLoading?: boolean;
  positionNames: string[];
  fallbackReviewerLabel?: string;
  onExited?: () => void;
};

const buildReviewerList = (
  positionNames: string[],
  positions:
    | {
        id: string;
        name: string;
        item_id: string;
        isResourcePosition?: boolean;
      }[]
    | undefined,
  roster: RosterEntry[] | undefined
): Reviewer[] => {
  const reviewers = positionNames.map((positionName) => {
    const position = positions?.find(
      (p) => p.name === positionName && !p.isResourcePosition
    );
    const users = (roster ?? [])
      .filter((m) =>
        m.incidentPositions.some((p) => p.positionName === positionName)
      )
      .map((m) => ({
        userGuid: m.userId,
        displayName: m.userName ?? m.emailAddress ?? m.userId,
        incidentMemberId: m.incidentMemberId,
        isOriginal: false,
      }));
    return {
      positionName,
      title: position ? positionName : `${positionName} (unassigned)`,
      positionId: position?.id ?? null,
      users,
    };
  });

  return markOriginalReviewers(reviewers);
};

/**
 * One original occupant per approval seat: unique holders first, then fill
 * gaps.
 */
const markOriginalReviewers = (reviewers: Reviewer[]): Reviewer[] => {
  const seatsByUser = new Map<string, string[]>();
  for (const reviewer of reviewers) {
    for (const user of reviewer.users) {
      const seats = seatsByUser.get(user.userGuid) ?? [];
      seats.push(reviewer.positionName);
      seatsByUser.set(user.userGuid, seats);
    }
  }

  const originalByPosition = new Map<string, Set<string>>();
  const claimed = new Set<string>();

  for (const reviewer of reviewers) {
    const originals = new Set<string>();
    for (const user of reviewer.users) {
      const seats = seatsByUser.get(user.userGuid) ?? [];
      if (seats.length === 1) {
        originals.add(user.userGuid);
        claimed.add(user.userGuid);
      }
    }
    originalByPosition.set(reviewer.positionName, originals);
  }

  for (const reviewer of reviewers) {
    const originals = originalByPosition.get(reviewer.positionName);
    if (!originals || originals.size > 0) {
      continue;
    }
    const candidate = reviewer.users.find(
      (user) => !claimed.has(user.userGuid)
    );
    if (candidate) {
      originals.add(candidate.userGuid);
      claimed.add(candidate.userGuid);
    }
  }

  return reviewers.map((reviewer) => {
    const originals =
      originalByPosition.get(reviewer.positionName) ?? new Set();
    const users = [...reviewer.users]
      .map((user) => ({
        ...user,
        isOriginal: originals.has(user.userGuid),
      }))
      .sort(
        (left, right) => Number(right.isOriginal) - Number(left.isOriginal)
      );
    return { ...reviewer, users };
  });
};

const getText = (positionName: string) => {
  if (positionName.includes('Situation Unit Leader')) {
    return 'Step 1 - Situation Unit Leader First Approval';
  }
  if (positionName.includes('Planning Section Chief')) {
    return 'Step 2 - Planning Section Chief Final Approval';
  }
  if (positionName.includes('Incident Commander')) {
    return 'Incident Commander - Override authority may approve at any time';
  }
  return positionName;
};

const RequestApprovalConfirmationModal: React.FC<
  RequestApprovalConfirmationModalProps
> = ({
  show,
  onHide,
  onConfirm,
  isSubmitting,
  name,
  createdBy,
  createdAt,
  editedBy,
  editedAt,
  incidentId,
  groupId,
  aorReviewers,
  approvalSteps,
  reviewersLoading = false,
  positionNames,
  fallbackReviewerLabel,
  onExited,
}) => {
  const aorText = useAorText();
  const isAorOnly = !incidentId;
  const policySteps = approvalSteps ?? [];
  const usesPolicy = isAorOnly && policySteps.length > 0;
  const [assigningPositionName, setAssigningPositionName] = useState<
    string | null
  >(null);
  const [addedByChoice, setAddedByChoice] = useState<Record<string, string[]>>(
    {}
  );
  const positionsQuery = useGetPositionsForIncidentId(incidentId ?? '');
  const rosterQuery = useGetRosterForIncident(incidentId ?? undefined);
  const groupRosterQuery = useGetRostersForGroup(groupId ?? '');
  const addUsersToIncident = useAddUsersToIncidentRoster(
    incidentId ?? '',
    groupId ?? ''
  );
  const removeMemberFromPosition = useRemoveMemberFromPositionInRoster(
    incidentId ?? '',
    groupId ?? ''
  );

  const reviewers = isAorOnly
    ? (aorReviewers ?? []).length > 0
      ? [
          {
            positionName: 'AOR SITREP Reviewers',
            title: aorText('AOR SITREP Reviewers'),
            positionId: null,
            users: (aorReviewers ?? []).map((r) => ({
              userGuid: r.reviewerItemId,
              displayName: r.displayName,
              incidentMemberId: null,
              isOriginal: true,
            })),
          },
        ]
      : []
    : buildReviewerList(positionNames, positionsQuery.data, rosterQuery.data);

  const assignOptionsByPosition = useMemo(() => {
    const groupMembers = groupRosterQuery.data ?? [];
    return Object.fromEntries(
      reviewers.map((reviewer) => {
        const assigned = new Set(reviewer.users.map((user) => user.userGuid));
        return [
          reviewer.positionName,
          groupMembers
            .filter((member) => !assigned.has(member.user_guid))
            .map((member) => ({
              value: member.user_guid,
              label:
                member.user_name?.trim() ||
                member.email_address ||
                member.user_guid,
            })),
        ];
      })
    );
  }, [groupRosterQuery.data, reviewers]);

  const isLoading =
    reviewersLoading ||
    (!!incidentId && (positionsQuery.isLoading || rosterQuery.isLoading));
  const isUpdatingRoster =
    addUsersToIncident.isPending || removeMemberFromPosition.isPending;

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const assignUserToReviewSeat = (
    reviewer: Reviewer,
    userGuid: string | null
  ) => {
    if (!userGuid || !incidentId || !groupId || !reviewer.positionId) {
      return;
    }
    addUsersToIncident.mutate(
      {
        positionAssignments: [
          {
            positionId: reviewer.positionId,
            positionName: reviewer.positionName,
            incidentMemberUserId: userGuid,
            isLeader: reviewer.users.length === 0,
          },
        ],
        isDraft: false,
      },
      {
        onSuccess: () => {
          setAddedByChoice((current) => {
            const seated = current[reviewer.positionName] ?? [];
            if (seated.includes(userGuid)) {
              return current;
            }
            return {
              ...current,
              [reviewer.positionName]: [...seated, userGuid],
            };
          });
          setAssigningPositionName(null);
        },
      }
    );
  };

  const removeChosenReviewer = (
    reviewer: Reviewer,
    user: Reviewer['users'][number]
  ) => {
    if (!reviewer.positionId || !user.incidentMemberId) {
      return;
    }
    removeMemberFromPosition.mutate(
      {
        incidentMemberId: user.incidentMemberId,
        positionId: reviewer.positionId,
        isDraft: false,
      },
      {
        onSuccess: () => {
          setAddedByChoice((current) => {
            const seated = (current[reviewer.positionName] ?? []).filter(
              (id) => id !== user.userGuid
            );
            return {
              ...current,
              [reviewer.positionName]: seated,
            };
          });
        },
      }
    );
  };

  const resetAssignmentState = () => {
    setAssigningPositionName(null);
    setAddedByChoice({});
  };

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) {
          resetAssignmentState();
          onHide();
          onExited?.();
        }
      }}
      title={`Send Draft Report: ${name.replace(/^Report:\s*/i, '').trim() || name}`}
      description={
        <>
          Created by {createdBy} at {formatTime(createdAt)}. Last edited by{' '}
          {editedBy} at {formatTime(editedAt)}.
        </>
      }
      widthClassName="tw:!max-w-2xl tw:sm:!max-w-2xl"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || isLoading || isUpdatingRoster}
          >
            {isSubmitting ? 'Sending' : 'Send for Review'}
          </Button>
        </>
      }
    >
      <div className="tw:space-y-3">
        <Card size="sm" className="tw:gap-2!">
          <CardHeader>
            <CardTitle>
              {isAorOnly
                ? aorText('Approval Workflow for AOR SITREPs')
                : 'Approval Workflow for Incident SITREPs'}
            </CardTitle>
          </CardHeader>
          <CardContent className="tw:text-sm tw:text-muted-foreground">
            {isAorOnly ? (
              usesPolicy ? (
                <p className="tw:m-0!">
                  This area uses its own approval steps. They run in order.
                  Anyone listed on the current step can approve it.
                </p>
              ) : (
                <p className="tw:m-0!">
                  Any user with permission to approve SITREPs for {name} can
                  approve this draft.
                </p>
              )
            ) : (
              <div className="tw:flex tw:flex-col tw:gap-1">
                <p className="tw:m-0!">
                  1. Send first to the <b>Situation Unit Leader</b> for review.
                </p>
                <p className="tw:m-0!">
                  2. Then routed to the <b>Planning Section Chief</b> for review
                  and approval.
                </p>
                <p className="tw:m-0!">
                  The <b>Incident Commander</b> can review and approve this
                  draft at any point, regardless of where it is in the workflow
                  above or the actions of the other reviewers.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        <Card size="sm" className="tw:gap-2!">
          <CardHeader>
            <CardTitle>Editing Locked after Submission</CardTitle>
          </CardHeader>
          <CardContent className="tw:text-sm tw:text-muted-foreground">
            <p className="tw:m-0!">
              Once this draft is submitted for review, only the listed reviewers
              {isAorOnly ? (
                ` for ${name} `
              ) : (
                <>
                  {' '}
                  (<b>Situation Unit Leader</b>, <b>Planning Section Chief</b>,
                  and <b>Incident Commander</b>){' '}
                </>
              )}
              will be able to edit it. You will not be able to edit the draft
              until a reviewer returns it.
            </p>
          </CardContent>
        </Card>
        <div className="tw:text-sm tw:font-medium">Notified Reviewers</div>
        {reviewersLoading ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            Loading approval settings…
          </p>
        ) : usesPolicy ? (
          <div className="tw:space-y-2">
            {policySteps.map((step) => (
              <Item key={step.title} variant="outline" size="sm">
                <ItemContent>
                  <ItemTitle>{step.title}</ItemTitle>
                  {step.people.length > 0 ? (
                    <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                      {step.people.map((person) => (
                        <Badge
                          key={person}
                          variant="secondary"
                          className="tw:h-6 tw:rounded-full"
                        >
                          {person}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <ItemDescription>
                      No one is assigned to this step
                    </ItemDescription>
                  )}
                </ItemContent>
              </Item>
            ))}
          </div>
        ) : reviewers.length === 0 ? (
          <p className="tw:m-0 tw:text-sm tw:italic tw:text-muted-foreground">
            {fallbackReviewerLabel ??
              'No reviewers are configured for this report.'}
          </p>
        ) : (
          <div className="tw:space-y-2">
            {reviewers.map((reviewer) => {
              const isAssigningThis =
                assigningPositionName === reviewer.positionName;
              const canAssign =
                !isAorOnly && !!reviewer.positionId && !!groupId;
              const chosenGuids = new Set(
                addedByChoice[reviewer.positionName] ?? []
              );
              return (
                <Item key={reviewer.positionName} variant="outline" size="sm">
                  <ItemContent>
                    <ItemTitle>{getText(reviewer.title)}</ItemTitle>
                    {reviewer.users.length > 0 ? (
                      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                        {reviewer.users.map((user) => {
                          const isPrimary =
                            user.isOriginal && !chosenGuids.has(user.userGuid);
                          const canRemove = canAssign && !isPrimary;
                          return (
                            <Badge
                              key={user.userGuid}
                              variant={isPrimary ? 'default' : 'secondary'}
                              className={
                                canRemove
                                  ? 'tw:h-6 tw:rounded-full tw:pr-1'
                                  : 'tw:h-6 tw:rounded-full'
                              }
                            >
                              {user.displayName}
                              {canRemove ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-xs"
                                  className="tw:-mr-0.5 tw:opacity-70 tw:hover:opacity-100"
                                  aria-label={`Remove ${user.displayName} from ${aorText(reviewer.positionName)}`}
                                  disabled={
                                    isSubmitting ||
                                    isUpdatingRoster ||
                                    !user.incidentMemberId
                                  }
                                  onClick={() =>
                                    removeChosenReviewer(reviewer, user)
                                  }
                                >
                                  <XIcon />
                                </Button>
                              ) : null}
                            </Badge>
                          );
                        })}
                      </div>
                    ) : (
                      <ItemDescription>
                        No user currently assigned
                      </ItemDescription>
                    )}
                  </ItemContent>
                  {canAssign ? (
                    <ItemActions>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-expanded={isAssigningThis}
                        disabled={isSubmitting || isUpdatingRoster}
                        onClick={() =>
                          setAssigningPositionName(
                            isAssigningThis ? null : reviewer.positionName
                          )
                        }
                      >
                        {isAssigningThis ? 'Cancel' : 'Assign'}
                      </Button>
                    </ItemActions>
                  ) : null}
                  {isAssigningThis && canAssign ? (
                    <div className="tw:basis-full tw:pt-1">
                      <SearchableSelect
                        aria-label={`Assign a user as ${aorText(reviewer.positionName)}`}
                        placeholder="Search org members…"
                        options={
                          assignOptionsByPosition[reviewer.positionName] ?? []
                        }
                        value={null}
                        disabled={isUpdatingRoster}
                        onChange={(next) =>
                          assignUserToReviewSeat(reviewer, next?.value ?? null)
                        }
                        positionerClassName="tw:z-[70]"
                      />
                    </div>
                  ) : null}
                </Item>
              );
            })}
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default RequestApprovalConfirmationModal;
